import {
  DollarSign, UtensilsCrossed, Package, Timer,
  Users, Trophy, Repeat, PhoneOutgoing,
} from 'lucide-react';
import {
  getSalesSummary, getMenuPerformance, getFoodCost, getServiceConsistency,
} from '../../services/reportingService';
import crmService from '../../services/crmService';
import apiClient from '../../services/api';

// Buffet analytics from the same endpoint the buffet module uses, date-ranged and optionally per branch.
const getBuffetAnalytics = async (restaurantId, filters = {}) => {
  const { data } = await apiClient.get(
    `/users/restaurant/${restaurantId}/buffets/analytics/`,
    {
      params: {
        location_id: filters.locationId || undefined,
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
      },
    },
  );
  if (!data?.success) throw new Error(data?.error || 'Failed to load buffet analytics');
  return data.data;
};

// The report list; each entry IS a separate report with its own page, fetch and filter.
export const REPORTS = [
  {
    key: 'sales',
    title: 'Sales & Revenue',
    description: 'Revenue, order count, average order value, daily trend and per-branch split.',
    Icon: DollarSign,
    fetch: getSalesSummary,
  },
  {
    key: 'menu',
    title: 'Menu Performance',
    description: 'Best-selling dishes by revenue and by units sold.',
    Icon: UtensilsCrossed,
    fetch: getMenuPerformance,
  },
  {
    key: 'food-cost',
    title: 'Food Cost & Inventory',
    description: 'Ingredient consumption, food-cost %, gross margin, liquor pour cost and low stock.',
    Icon: Package,
    fetch: getFoodCost,
  },
  {
    key: 'service',
    title: 'Service & Consistency',
    description: 'Fulfilment time, table turnaround, covers and cancellation / void rates.',
    Icon: Timer,
    fetch: getServiceConsistency,
  },
  {
    key: 'buffet',
    title: 'Buffet Reports',
    description: 'Buffet covers, revenue, ARPC, food-cost %, break-even covers and waste — overall and per buffet.',
    // Now lives under Open Buffet Insights: hidden from the grid, but the /reports/buffet route keeps working.
    hidden: true,
    Icon: UtensilsCrossed,
    fetch: getBuffetAnalytics,
  },
  // CRM guest reports are lifetime views scoped by the staff JWT, so `noFilters` hides the date bar.
  {
    key: 'crm-segments',
    title: 'Guest Segments (RFM)',
    description: 'Every guest scored on Recency · Frequency · Monetary and grouped into segments with a suggested play.',
    Icon: Users,
    noFilters: true,
    fetch: () => crmService.rfm(),
  },
  {
    key: 'crm-top-customers',
    title: 'Top Customers',
    description: 'Lifetime value ranking — your VIP list with spend rate and projected annual value.',
    Icon: Trophy,
    noFilters: true,
    fetch: () => crmService.topCustomers(50),
  },
  {
    key: 'crm-retention',
    title: 'Retention',
    description: 'Repeat rate and the monthly mix of new vs returning guests.',
    Icon: Repeat,
    noFilters: true,
    fetch: () => crmService.retention(12),
  },
  {
    key: 'crm-menu-magnets',
    title: 'Menu Magnets',
    description: 'Dishes that bring guests back — repeat-order rate per dish.',
    Icon: UtensilsCrossed,
    noFilters: true,
    fetch: () => crmService.menuAffinity(25),
  },
  {
    key: 'crm-winback',
    title: 'Win-back List',
    description: 'Regulars going quiet — overdue vs their own visiting rhythm, with value at risk.',
    Icon: PhoneOutgoing,
    noFilters: true,
    fetch: () => crmService.churnRisk(100),
  },
];

export const reportByKey = (key) => REPORTS.find((r) => r.key === key) || null;
